import React, { useEffect, useState } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { motion } from "framer-motion";
import { CheckCircle2 } from "lucide-react";
import Navbar from "@/components/campus/Navbar";
import Footer from "@/components/campus/Footer";
import { api } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { toast } from "sonner";

const CHECKLIST = [
  "We contact you within 24 hours",
  "Dedicated support agent assigned to you",
  "Application form filled — zero errors",
  "All documents prepared and verified",
  "Deadlines tracked, never missed",
  "We coordinate directly with the college",
  "Hostel booking handled for you",
  "Support until you're fully settled in",
];

export default function Decided() {
  const { slug } = useParams();
  const { user, loading } = useAuth();
  const [college, setCollege] = useState(null);
  const [time, setTime] = useState("evening");
  const [notes, setNotes] = useState("");
  const [phone, setPhone] = useState("");
  const [submitted, setSubmitted] = useState(false);
  const nav = useNavigate();

  useEffect(()=>{
    if (!loading && !user) { nav(`/auth?mode=login&next=/decided/${slug}`); return; }
    api.getCollege(slug).then(setCollege).catch(()=>{ toast.error("College not found"); nav("/"); });
    if (user) setPhone(user.phone);
  }, [slug, user, loading]);

  const submit = async (e) => {
    e.preventDefault();
    try { await api.createDecision({ college_name: college.name, preferred_contact_time: time, notes, phone }); setSubmitted(true); }
    catch { toast.error("Failed to submit"); }
  };

  if (!college) return null;

  return (
    <div className="min-h-screen bg-[#0F172A] text-white">
      <Navbar/>
      <section className="pt-28 px-6 pb-20 max-w-3xl mx-auto">
        {!submitted ? (
          <>
            <div className="text-xs font-bold tracking-[0.25em] uppercase text-amber-400">You've decided</div>
            <h1 className="font-display text-4xl md:text-5xl font-bold mt-3"><span className="bg-gradient-to-r from-[#2563EB] to-[#60A5FA] bg-clip-text text-transparent">{college.name}</span></h1>
            <p className="text-slate-300 mt-3">Great choice! Our team will handle your entire admission process — completely free of charge.</p>

            <div className="glass rounded-2xl p-6 mt-8">
              <h3 className="font-semibold mb-3">What our team handles</h3>
              <div className="space-y-2.5">{CHECKLIST.map((t,i)=>(
                <motion.div key={i} initial={{opacity:0,x:-10}} animate={{opacity:1,x:0}} transition={{delay:i*0.08}} className="flex items-center gap-2 text-sm text-slate-200"><CheckCircle2 className="text-amber-400 shrink-0" size={16}/>{t}</motion.div>
              ))}</div>
            </div>

            <form onSubmit={submit} className="glass rounded-2xl p-6 mt-6 space-y-4">
              <h3 className="font-semibold">Your details</h3>
              <div><label className="text-xs text-slate-400 block mb-1">Phone (we'll call here)</label><input value={phone} onChange={e=>setPhone(e.target.value)} required className="w-full bg-[#1E293B] border border-white/10 rounded-lg px-4 py-2.5"/></div>
              <div><label className="text-xs text-slate-400 block mb-1">Best time to call</label>
                <div className="grid grid-cols-3 gap-2">{["morning","afternoon","evening"].map(t=><button key={t} type="button" onClick={()=>setTime(t)} className={`py-2 rounded-lg text-sm border capitalize ${time===t?"bg-[#2563EB] border-[#2563EB]":"border-white/10 text-slate-300"}`}>{t}</button>)}</div>
              </div>
              <div><label className="text-xs text-slate-400 block mb-1">Any concerns? (optional)</label><textarea value={notes} onChange={e=>setNotes(e.target.value)} placeholder="e.g. I'm confused between CSE and IT branch" className="w-full bg-[#1E293B] border border-white/10 rounded-lg px-4 py-2.5 min-h-[80px]"/></div>
              <button data-testid="submit-decision" className="w-full py-3 rounded-full bg-[#F59E0B] text-[#0F172A] font-bold">Submit</button>
            </form>
          </>
        ) : (
          <motion.div initial={{scale:0.9,opacity:0}} animate={{scale:1,opacity:1}} className="glass rounded-2xl p-10 text-center">
            <motion.svg initial={{pathLength:0}} animate={{pathLength:1}} transition={{duration:0.8}} className="w-24 h-24 mx-auto text-emerald-400" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><motion.path d="M5 13l4 4L19 7" initial={{pathLength:0}} animate={{pathLength:1}} transition={{duration:0.8}}/></motion.svg>
            <h2 className="font-display text-3xl font-bold mt-5">You're all set! 🎉</h2>
            <p className="text-slate-400 mt-2">Our team will contact you at {phone} within 24 hours.</p>
            <p className="text-slate-400 mt-2 text-sm">Meanwhile, feel free to ask more questions to Campus Ambassadors.</p>
            <button onClick={()=>nav("/")} className="mt-8 px-6 py-3 rounded-full bg-[#2563EB] hover:bg-blue-600 font-semibold">Back to Home</button>
          </motion.div>
        )}
      </section>
      <Footer/>
    </div>
  );
}
